import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Bot, Pause, Play, Power, RefreshCw, PhoneCall, PhoneOff, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { VAPI_PUBLIC_KEY } from "@/lib/api";
import { toast } from "sonner";

export default function AdminAI() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const isSuper = admin?.admin_role === "super_admin";
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [testing, setTesting] = useState(null);
  const vapiRef = useRef(null);

  const load = () => adminApi.get("/admin/customers", { params: { page: 1, page_size: 500 } })
    .then((r) => setRows(r.data.customers.filter((c) => c.has_assistant))).catch(() => {});
  useEffect(() => { load(); }, []);

  const action = async (uid, act) => {
    setBusy(uid + act);
    try {
      const r = await adminApi.post(`/admin/ai/${uid}/${act}`);
      toast.success(act === "regenerate" ? "AI regenerated" : `AI ${act}d`);
      if (r.data.vapi_assistant_id) toast.info(`New assistant: ${r.data.vapi_assistant_id.slice(0, 8)}…`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const testAI = async (c) => {
    if (testing) { vapiRef.current?.stop(); setTesting(null); return; }
    if (!VAPI_PUBLIC_KEY) return toast.error("Voice key not configured");
    try {
      setTesting(c.user_id);
      const { default: Vapi } = await import("@vapi-ai/web");
      const v = new Vapi(VAPI_PUBLIC_KEY); vapiRef.current = v;
      v.on("call-end", () => setTesting(null));
      v.on("error", () => { setTesting(null); toast.error("Could not start test call (check Vapi credits)"); });
      // fetch the assistant id
      const d = await adminApi.get(`/admin/customers/${c.user_id}`);
      const aid = d.data.business?.vapi_assistant_id;
      if (!aid) { setTesting(null); return toast.error("No assistant to test"); }
      v.start(aid);
      toast.success(`Testing ${c.business_name}'s AI — speak now`);
    } catch (e) { setTesting(null); toast.error("Test failed"); }
  };

  if (!rows) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-ai">
      <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Bot className="h-6 w-6 text-blue-600" /> AI Management</h1>
      <p className="text-slate-500 mt-1 text-sm">{isSuper ? "Manage every customer's AI receptionist." : "View only — actions require Super Admin."}</p>
      <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left font-semibold px-4 py-3">Business</th><th className="text-left font-semibold px-4 py-3">Assistant ID</th>
            <th className="text-left font-semibold px-4 py-3">Health</th><th className="text-left font-semibold px-4 py-3">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={4} className="py-12 text-center text-slate-400">No AI receptionists created yet</td></tr>}
            {rows.map((c) => (
              <tr key={c.user_id} data-testid={`ai-row-${c.user_id}`}>
                <td className="px-4 py-3 font-semibold">{c.business_name}<div className="text-xs text-slate-400 font-normal">{c.owner}</div></td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{c.has_assistant ? "configured" : "—"}</td>
                <td className="px-4 py-3">{c.ai_live ? <span className="text-emerald-600 text-xs font-semibold">● Live</span> : <span className="text-amber-600 text-xs font-semibold">● Test mode</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <Button data-testid={`ai-test-${c.user_id}`} size="sm" variant="outline" className="h-7 gap-1" onClick={() => testAI(c)}>
                      {testing === c.user_id ? <><PhoneOff className="h-3 w-3" /> Stop</> : <><PhoneCall className="h-3 w-3" /> Test</>}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => navigate(`/admin/customers/${c.user_id}`)}><ExternalLink className="h-3 w-3" /> Config</Button>
                    {isSuper && <>
                      {c.ai_live
                        ? <Button data-testid={`ai-pause-${c.user_id}`} size="sm" variant="outline" className="h-7 gap-1" disabled={busy === c.user_id + "pause"} onClick={() => action(c.user_id, "pause")}><Pause className="h-3 w-3" /> Pause</Button>
                        : <Button data-testid={`ai-resume-${c.user_id}`} size="sm" variant="outline" className="h-7 gap-1" disabled={busy === c.user_id + "resume"} onClick={() => action(c.user_id, "resume")}><Play className="h-3 w-3" /> Resume</Button>}
                      <Button size="sm" variant="outline" className="h-7 gap-1 text-rose-600" disabled={busy === c.user_id + "deactivate"} onClick={() => action(c.user_id, "deactivate")}><Power className="h-3 w-3" /> Deactivate</Button>
                      <Button data-testid={`ai-regen-${c.user_id}`} size="sm" variant="outline" className="h-7 gap-1" disabled={busy === c.user_id + "regenerate"} onClick={() => action(c.user_id, "regenerate")}>{busy === c.user_id + "regenerate" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Regenerate</Button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
